import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from './ui/button';
import ContactFormModal from './ContactFormModal';

const HeroSection = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <>
      <section className="relative bg-gradient-to-br from-slate-50 via-white to-indigo-50 py-20 overflow-hidden">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 border-4 border-indigo-600 rounded-lg"></div>
          <div className="absolute bottom-20 left-1/4 w-24 h-24 border-4 border-amber-500 rounded-lg"></div>
          <div className="absolute top-1/3 right-20 w-28 h-28 border-4 border-indigo-600 rounded-lg"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="space-y-8">
              <div>
                <h2 className="text-5xl md:text-6xl font-bold leading-tight">
                  <span className="text-indigo-600">Protected,</span>
                  <br />
                  <span className="text-gray-900">Trusted Email for Professionals</span>
                </h2>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3 group transition-transform duration-300 hover:translate-x-2">
                  <Check className="h-6 w-6 text-amber-500 mt-1 flex-shrink-0" strokeWidth={3} />
                  <p className="text-lg text-gray-700">
                    Safeguard your communications with Guarded Mail Box, a fully encrypted email platform
                  </p>
                </div>

                <div className="flex items-start gap-3 group transition-transform duration-300 hover:translate-x-2">
                  <Check className="h-6 w-6 text-amber-500 mt-1 flex-shrink-0" strokeWidth={3} />
                  <p className="text-lg text-gray-700">
                    Deploy your professional email on a secure, privacy-first, and tracker-free platform
                  </p>
                </div>

                <div className="flex items-start gap-3 group transition-transform duration-300 hover:translate-x-2">
                  <Check className="h-6 w-6 text-amber-500 mt-1 flex-shrink-0" strokeWidth={3} />
                  <p className="text-lg text-gray-700">
                    Eliminate surveillance and tracking from your inbox forever
                  </p>
                </div>
              </div>
            </div>

            {/* Right illustration */}
            <div className="relative">
              <div className="relative z-10">
                <svg viewBox="0 0 400 400" className="w-full h-auto drop-shadow-2xl">
                  {/* Shield base */}
                  <defs>
                    <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#4F46E5', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#6366F1', stopOpacity: 1 }} />
                    </linearGradient>
                    <linearGradient id="accentGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" style={{ stopColor: '#F59E0B', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#FBBF24', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  
                  {/* Main shield */}
                  <path
                    d="M200 30 L320 80 L320 180 Q320 280 200 370 Q80 280 80 180 L80 80 Z"
                    fill="url(#shieldGradient)"
                    className="animate-pulse"
                    style={{ animationDuration: '3s' }}
                  />
                  
                  {/* Shield border */}
                  <path
                    d="M200 30 L320 80 L320 180 Q320 280 200 370 Q80 280 80 180 L80 80 Z"
                    fill="none"
                    stroke="url(#accentGradient)"
                    strokeWidth="4"
                  />
                  
                  {/* Center lock icon */}
                  <rect x="175" y="150" width="50" height="60" rx="5" fill="white" opacity="0.9" />
                  <circle cx="200" cy="140" r="20" fill="none" stroke="white" strokeWidth="6" opacity="0.9" />
                  <circle cx="200" cy="175" r="8" fill="#4F46E5" />
                  <rect x="197" y="175" width="6" height="20" fill="#4F46E5" />
                  
                  {/* Decorative elements */}
                  <circle cx="120" cy="120" r="4" fill="#F59E0B" opacity="0.6">
                    <animate attributeName="opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="280" cy="140" r="4" fill="#F59E0B" opacity="0.6">
                    <animate attributeName="opacity" values="0.6;1;0.6" dur="2.5s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="150" cy="250" r="4" fill="#F59E0B" opacity="0.6">
                    <animate attributeName="opacity" values="0.6;1;0.6" dur="3s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="250" cy="260" r="4" fill="#F59E0B" opacity="0.6">
                    <animate attributeName="opacity" values="0.6;1;0.6" dur="2.2s" repeatCount="indefinite" />
                  </circle>
                </svg>
              </div>
              
              {/* Background glow effect */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-400 rounded-full filter blur-3xl opacity-20 animate-pulse" style={{ animationDuration: '4s' }}></div>
            </div>
          </div>
        </div>
      </section>

      <ContactFormModal 
        isOpen={isContactOpen} 
        onClose={() => setIsContactOpen(false)}
        title="Get Started with Guarded Mail Box"
      />
    </>
  );
};

export default HeroSection;